import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { KYCState, KYCData, mapProblemsToSteps } from "@/types/models/kyc";

const initialState: KYCState = {
  isLoading: false,
  error: null,
  currentStep: 1,
  kycStatus: "PENDING",
  rejectionReason: undefined,
  kycProblems: undefined,
  rejectedSteps: {
    businessDetails: false,
    personalDetails: false,
    documentUpload: false,
    addressVerification: false,
    videoVerification: false,
  },
  completedSteps: {
    businessDetails: false,
    personalDetails: false,
    documentUpload: false,
    addressVerification: false,
    videoVerification: false,
  },
  kycData: undefined,
  isResubmissionMode: false, // Track if user is resubmitting after rejection
};

const kycSlice = createSlice({
  name: "kyc",
  initialState,
  reducers: {
    // Fetch KYC Status Actions
    fetchKYCStatusRequest: (
      state,

      _action: PayloadAction<{ storeId: string }>,
    ) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchKYCStatusSuccess: (state, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.error = null;
      // Debug log for incoming payload

      console.debug(
        "[kyc.slice] fetchKYCStatusSuccess payload:",
        action.payload,
      );
      // API sometimes returns { message, valid, data: { ... } }
      // Normalize to the inner data object if present, otherwise use payload directly
      const payloadData =
        action.payload && (action.payload as any).data
          ? (action.payload as any).data
          : action.payload;
      state.kycData = payloadData as KYCData;

      console.debug("[kyc.slice] normalized kycData stored:", state.kycData);

      // Map backend status and stage to frontend state
      const backendStatus = (payloadData as any).status;
      const backendStage = (payloadData as any).stage;
      let frontendStatus: KYCState["kycStatus"] = "PENDING";

      // Map backend KycStatus enum to frontend status
      switch (backendStatus) {
        case "approved":
          frontendStatus = "APPROVED";
          break;
        case "rejected":
          frontendStatus = "REJECTED";
          break;
        case "under_review":
          frontendStatus = "PENDING_VERIFICATION";
          break;
        case "pending":
        default:
          // For pending status, determine if it's in progress or truly pending
          if (backendStage === "completed") {
            frontendStatus = "PENDING_VERIFICATION";
          } else {
            frontendStatus = "IN_PROGRESS";
          }
          break;
      }

      state.kycStatus = frontendStatus;
      state.rejectionReason = (payloadData as any).rejection_reason;

      // Handle KYC problems and rejection details
      if (
        (payloadData as any).kyc_problem &&
        (payloadData as any).kyc_problem.length > 0
      ) {
        state.kycProblems = (payloadData as any).kyc_problem;
        state.rejectedSteps = mapProblemsToSteps(
          (payloadData as any).kyc_problem,
        );
        // If there are problems, enable resubmission mode
        state.isResubmissionMode = true;
      } else {
        state.kycProblems = [];
        state.isResubmissionMode = false;
        // Reset rejected steps if no problems
        state.rejectedSteps = {
          businessDetails: false,
          personalDetails: false,
          documentUpload: false,
          addressVerification: false,
          videoVerification: false,
        };
      }

      // Update completed steps based on actual KYC data presence
      state.completedSteps = {
        businessDetails: !!(
          (payloadData as any).business_name &&
          (payloadData as any).business_email &&
          (payloadData as any).business_phone_number
        ),
        personalDetails: !!(payloadData as any).gst_number,
        documentUpload: !!(
          (payloadData as any).aadhaar_document_front &&
          (payloadData as any).aadhaar_document_back &&
          (payloadData as any).pan_document
        ),
        addressVerification: !!(payloadData as any).address_proof_document,
        videoVerification: !!(payloadData as any).video_file,
      };

      // Determine current step based on backend stage and status
      const newStep = determineCurrentStepFromBackend(
        backendStage,
        backendStatus,
        state.completedSteps,
        state.rejectedSteps,
      );
      state.currentStep = newStep;
    },
    fetchKYCStatusFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    // New action for handling new users (404 case)
    fetchKYCStatusNotFound: (state) => {
      state.isLoading = false;
      state.error = null;
      // Reset state for new users (404 case - no KYC record exists)
      // This corresponds to backend stage "not_started"
      state.kycData = undefined;
      state.kycStatus = "PENDING";
      state.rejectionReason = undefined;
      state.kycProblems = undefined;
      state.rejectedSteps = {
        businessDetails: false,
        personalDetails: false,
        documentUpload: false,
        addressVerification: false,
        videoVerification: false,
      };
      state.completedSteps = {
        businessDetails: false,
        personalDetails: false,
        documentUpload: false,
        addressVerification: false,
        videoVerification: false,
      };
      state.isResubmissionMode = false;
      // For 404/not_started, always start at step 1
      state.currentStep = 1;
    },

    // Business Details Actions
    submitBusinessDetailsRequest: (
      state,

      _action: PayloadAction<{ storeId: string; formData: FormData }>,
    ) => {
      state.isLoading = true;
      state.error = null;
    },
    submitBusinessDetailsSuccess: (
      state,
      action: PayloadAction<{ submittedData?: Partial<KYCData> }>,
    ) => {
      state.isLoading = false;
      state.error = null;
      state.completedSteps.businessDetails = true;
      state.lastAction = "submitBusinessDetailsSuccess";

      // Preserve submitted data in Redux state so form doesn't lose user input
      if (action.payload?.submittedData) {
        state.kycData = {
          ...state.kycData,
          ...action.payload.submittedData,
        } as KYCData;
      }

      // Update current step based on completion
      const newStep = determineCurrentStep(
        state.completedSteps,
        state.kycStatus,
      );
      state.currentStep = newStep;
    },
    submitBusinessDetailsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.lastAction = "submitBusinessDetailsFailure";
    },

    // Personal Details Actions
    submitPersonalDetailsRequest: (
      state,

      _action: PayloadAction<{ storeId: string; formData: FormData }>,
    ) => {
      state.isLoading = true;
      state.error = null;
    },
    submitPersonalDetailsSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.completedSteps.personalDetails = true;
      // Update current step based on completion
      state.currentStep = determineCurrentStep(
        state.completedSteps,
        state.kycStatus,
      );
    },
    submitPersonalDetailsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Document Upload Actions
    uploadDocumentsRequest: (
      state,

      _action: PayloadAction<{
        storeId: string;
        formData: FormData;
        isUpdate?: boolean;
      }>,
    ) => {
      state.isLoading = true;
      state.error = null;
    },
    uploadDocumentsSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.completedSteps.documentUpload = true;
      state.isResubmissionMode = false; // Reset resubmission mode on success
      state.currentStep = determineCurrentStep(
        state.completedSteps,
        state.kycStatus,
      );
    },
    uploadDocumentsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Address Verification Actions
    uploadAddressProofRequest: (
      state,

      _action: PayloadAction<{
        storeId: string;
        formData: FormData;
        isUpdate?: boolean;
      }>,
    ) => {
      state.isLoading = true;
      state.error = null;
    },
    uploadAddressProofSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.completedSteps.addressVerification = true;
      state.isResubmissionMode = false; // Reset resubmission mode on success
      state.currentStep = determineCurrentStep(
        state.completedSteps,
        state.kycStatus,
      );
    },
    uploadAddressProofFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Video Verification Actions
    uploadVideoKYCRequest: (
      state,

      _action: PayloadAction<{
        storeId: string;
        formData: FormData;
        isUpdate?: boolean;
      }>,
    ) => {
      state.isLoading = true;
      state.error = null;
    },
    uploadVideoKYCSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.completedSteps.videoVerification = true;
      state.kycStatus = "PENDING_VERIFICATION";
      state.isResubmissionMode = false; // Reset resubmission mode on success
      state.currentStep = determineCurrentStep(
        state.completedSteps,
        state.kycStatus,
      );
    },
    uploadVideoKYCFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Navigation Actions
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    goToNextStep: (state) => {
      if (state.currentStep < 6) {
        state.currentStep += 1;
      }
    },
    goToPreviousStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },

    // Utility Actions
    clearError: (state) => {
      state.error = null;
    },
    resetKYCState: () => initialState,

    // Mark step as complete (for testing/debugging)
    markStepAsComplete: (
      state,
      action: PayloadAction<keyof KYCState["completedSteps"]>,
    ) => {
      state.completedSteps[action.payload] = true;
    },

    // Navigate to first rejected step for resubmission
    navigateToFirstRejectedStep: (state) => {
      state.isResubmissionMode = true;
      if (state.rejectedSteps) {
        if (state.rejectedSteps.businessDetails) {
          state.currentStep = 1;
        } else if (state.rejectedSteps.personalDetails) {
          state.currentStep = 2;
        } else if (state.rejectedSteps.documentUpload) {
          state.currentStep = 3;
        } else if (state.rejectedSteps.addressVerification) {
          state.currentStep = 4;
        } else if (state.rejectedSteps.videoVerification) {
          state.currentStep = 5;
        }
      }
    },

    // PATCH Update Actions (for resubmissions)
    updateKycFieldsRequest: (
      state,

      _action: PayloadAction<{
        storeId: string;
        fields: Record<string, unknown>;
      }>,
    ) => {
      state.isLoading = true;
      state.error = null;
    },

    updateKycFieldsSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      // Reset resubmission mode and refresh KYC status
      state.isResubmissionMode = false;
    },

    updateKycFieldsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

// Helper function to determine current step based on backend stage and status
function determineCurrentStepFromBackend(
  backendStage: string,
  backendStatus: string,
  completedSteps: KYCState["completedSteps"],
  rejectedSteps: KYCState["rejectedSteps"],
): number {
  // If KYC is approved, rejected, or under review, show status page
  if (
    backendStatus === "approved" ||
    backendStatus === "rejected" ||
    backendStatus === "under_review"
  ) {
    return 6; // Status display (UI Step 6)
  }

  // For rejected KYC with problems, go to the first rejected step
  if (backendStatus === "rejected") {
    if (rejectedSteps?.businessDetails) return 1;
    if (rejectedSteps?.personalDetails) return 2;
    if (rejectedSteps?.documentUpload) return 3;
    if (rejectedSteps?.addressVerification) return 4;
    if (rejectedSteps?.videoVerification) return 5;
  }

  // Map backend stage enum to frontend steps
  switch (backendStage) {
    case "not_started":
      return 1; // Business Details
    case "business_details":
      return 2; // Personal Details
    case "personal_details":
      return 3; // Document Upload
    case "document_upload":
      return 4; // Address Verification
    case "address_verification":
      return 5; // Video Verification
    case "video_verification":
    case "completed":
      return 6; // Status display
    default:
      // Fallback: determine based on completed steps
      return determineCurrentStepFromCompletedSteps(completedSteps);
  }
}

// Fallback helper function to determine current step based on completion status
function determineCurrentStepFromCompletedSteps(
  completedSteps: KYCState["completedSteps"],
): number {
  // 6-step flow: Business → Personal → Documents → Address → Video → Status
  if (!completedSteps.businessDetails) {
    return 1;
  }
  if (!completedSteps.personalDetails) {
    return 2;
  }
  if (!completedSteps.documentUpload) {
    return 3;
  }
  if (!completedSteps.addressVerification) {
    return 4;
  }
  if (!completedSteps.videoVerification) {
    return 5;
  }

  return 6; // Default to status if all else is done
}

// Legacy helper function for backward compatibility
function determineCurrentStep(
  completedSteps: KYCState["completedSteps"],
  kycStatus: KYCState["kycStatus"],
): number {
  // Check for final statuses first
  if (
    kycStatus === "APPROVED" ||
    kycStatus === "REJECTED" ||
    kycStatus === "PENDING_VERIFICATION"
  ) {
    return 6; // Status display (UI Step 6)
  }

  return determineCurrentStepFromCompletedSteps(completedSteps);
}

export const {
  // Fetch KYC Status
  fetchKYCStatusRequest,
  fetchKYCStatusSuccess,
  fetchKYCStatusFailure,
  fetchKYCStatusNotFound,

  // Business Details
  submitBusinessDetailsRequest,
  submitBusinessDetailsSuccess,
  submitBusinessDetailsFailure,

  // Personal Details
  submitPersonalDetailsRequest,
  submitPersonalDetailsSuccess,
  submitPersonalDetailsFailure,

  // Document Upload
  uploadDocumentsRequest,
  uploadDocumentsSuccess,
  uploadDocumentsFailure,

  // Address Verification
  uploadAddressProofRequest,
  uploadAddressProofSuccess,
  uploadAddressProofFailure,

  // Video Verification
  uploadVideoKYCRequest,
  uploadVideoKYCSuccess,
  uploadVideoKYCFailure,

  // PATCH Update (for resubmissions)
  updateKycFieldsRequest,
  updateKycFieldsSuccess,
  updateKycFieldsFailure,

  // Navigation
  setCurrentStep,
  goToNextStep,
  goToPreviousStep,

  // Utility
  clearError,
  resetKYCState,
  markStepAsComplete,
  navigateToFirstRejectedStep,
} = kycSlice.actions;

export default kycSlice.reducer;
